import ky from 'ky';

import { clearAuth, useAuthStore } from '../stores/authStore';

import { env } from '@/lib/env';

let inflight: Promise<string | null> | null = null;

/**
 * Single-flight refresh. Multiple concurrent 401s collapse into one
 * `/auth/refresh` request; everyone awaits the same promise and gets the
 * fresh access token (or null on failure). On failure, the auth store is
 * cleared so route guards can redirect to login.
 *
 * The backend issues the refresh token as an httpOnly cookie, so the
 * frontend never holds or sends it explicitly — `credentials: 'include'`
 * is enough.
 */
export function refreshAccessToken(): Promise<string | null> {
  if (inflight) return inflight;
  inflight = doRefresh().finally(() => {
    inflight = null;
  });
  return inflight;
}

async function doRefresh(): Promise<string | null> {
  try {
    const response = await ky.post(`${env.VITE_API_URL}/auth/refresh`, {
      credentials: 'include',
      timeout: 10_000,
      retry: 0,
    });
    const body: { accessToken?: unknown } = await response.json();
    if (typeof body.accessToken !== 'string' || body.accessToken.length === 0) {
      clearAuth();
      return null;
    }
    useAuthStore.getState().setAccessToken(body.accessToken);
    return body.accessToken;
  } catch {
    clearAuth();
    return null;
  }
}
